import React from 'react';
import Konva from 'konva';
import useNodeDimensionsOperations from 'components/canvas/hooks/use-node-dimensions-operations';
import useSurfaceNodesFromCache from 'components/canvas/hooks/use-surface-nodes-from-cache';
import useCurrentSurface from 'routes/surface/hooks/use-current-surface';
import { makeVar, useReactiveVar } from '@apollo/client';
import { isFunction, get } from 'lodash';
import { AnyObject, Position } from 'types';
import { DeleteConfirmModalContext } from 'routes/surface/components/delete-confirm-modal';

interface UseCanvas {
  stageRef: React.RefObject<Konva.Stage>;
  stageInstance?: Konva.Stage;
  layerInstance?: Konva.Layer;
  layerRef: React.RefObject<Konva.Layer>;
  deleteConfirmModalRef: React.RefObject<DeleteConfirmModalContext>;
  findShapeById(input: FindShapeByIdInput): any;
  findShapeByKonvaAttributes(attributes: AnyObject): any;
  getPointerPosition(): Position | null | undefined;
  getRelativePointerPosition(): Position | null | undefined;
  setGrabbingCursor(): void;
  setPointerCursor(): void;
  setDefaultCursor(): void;
  findMostLeftPoint(): number;
  findMostRightPoint(): number;
  findMostLowestPoint(): number;
  findHighestPoint(): number;
}

type FindShapeByIdInput = ((node: any) => boolean) | string;

export const stageRefVar = makeVar<React.RefObject<Konva.Stage>>(
  React.createRef<Konva.Stage>(),
);
export const layerRefVar = makeVar<React.RefObject<Konva.Layer>>(
  React.createRef<Konva.Layer>(),
);

export const deleteConfirmModalRefVar = makeVar<
  React.RefObject<DeleteConfirmModalContext>
>(React.createRef<DeleteConfirmModalContext>());

export default function useCanvas(): UseCanvas {
  const stageRef = useReactiveVar(stageRefVar);
  const layerRef = useReactiveVar(layerRefVar);
  const deleteConfirmModalRef = useReactiveVar(deleteConfirmModalRefVar);

  const stageInstance = stageRef.current?.getStage();
  const layerInstance = layerRef.current?.getLayer();

  const { getNodeDimensions } = useNodeDimensionsOperations();

  const surface = useCurrentSurface();

  const nodes = useSurfaceNodesFromCache(surface?.id);

  function findShapeById(input: FindShapeByIdInput): any {
    // @ts-ignore
    const result = stageRef.current?.find((shape) =>
      isFunction(input) ? input(shape) : shape.attrs.id === input,
    ) as any;

    if (Array.isArray(result)) {
      return result[0];
    }
    return result;
  }

  function findShapeByKonvaAttributes(attributes: AnyObject): any {
    const result = stageRef.current?.find((shape) =>
      Object.entries(attributes).every(
        (key, value) => get(shape.attrs, key) === value,
      ),
    ) as any;

    if (Array.isArray(result)) {
      return result[0];
    }
    return result;
  }

  function getPointerPosition(): Position | null | undefined {
    return stageRef.current?.getPointerPosition();
  }

  function getRelativePointerPosition(): Position | null {
    if (stageInstance) {
      const position = stageInstance?.getPointerPosition() as Position;

      return {
        x: (position.x - stageInstance.x()) / stageInstance.scaleX(),
        y: (position.y - stageInstance.y()) / stageInstance.scaleX(),
      };
    }

    return null;
  }

  function setGrabbingCursor() {
    const container = stageRef.current?.getStage()?.container();

    if (container) {
      container.style.cursor = 'grabbing';
    }
  }

  function setPointerCursor() {
    const container = stageRef.current?.getStage()?.container();

    if (container) {
      container.style.cursor = 'pointer';
    }
  }

  function setDefaultCursor() {
    const container = stageRef.current?.getStage()?.container();

    if (container) {
      container.style.cursor = 'default';
    }
  }

  function findMostLeftPoint() {
    //@ts-ignore
    const mostLeftX = Math.min(
      ...nodes.map(({ position }) => position?.x ?? 0),
    );

    const mostLeftNode = nodes.find((node) => node?.position?.x === mostLeftX);

    if (mostLeftNode && mostLeftNode.type === 'SUB_SURFACE') {
      const mostLeftNodeWidth = getNodeDimensions(mostLeftNode).width;

      return mostLeftX - mostLeftNodeWidth / 2;
    }

    return mostLeftX;
  }

  function findMostRightPoint() {
    const mostRightX = Math.max(
      ...nodes.map(({ position }) => position?.x ?? 0),
    );

    const mostRightNode = nodes.find(
      (node) => node?.position?.x === mostRightX,
    );

    if (mostRightNode) {
      const mostRightNodeWidth = getNodeDimensions(mostRightNode).width;

      if (mostRightNode.type === 'SUB_SURFACE') {
        return mostRightX + mostRightNodeWidth / 2;
      }

      return mostRightX + mostRightNodeWidth;
    }

    return mostRightX;
  }

  function findHighestPoint() {
    const highestY = Math.min(...nodes.map(({ position }) => position?.y ?? 0));

    const highestNode = nodes.find((node) => node?.position?.y === highestY);

    if (highestNode && highestNode.type === 'SUB_SURFACE') {
      const highestNodeHeight = getNodeDimensions(highestNode).height;

      return highestY - highestNodeHeight / 2;
    }

    return highestY;
  }

  function findMostLowestPoint() {
    const lowestY = Math.max(...nodes.map(({ position }) => position?.y ?? 0));

    const lowestNode = nodes.find((node) => node?.position?.y === lowestY);

    if (lowestNode) {
      const lowestNodeHeight = getNodeDimensions(lowestNode).height;

      return lowestY < 0
        ? lowestY - lowestNodeHeight
        : lowestY + lowestNodeHeight;
    }

    return lowestY;
  }

  return {
    stageRef,
    layerRef,
    layerInstance,
    stageInstance,
    deleteConfirmModalRef,
    findShapeById,
    getPointerPosition,
    getRelativePointerPosition,
    setGrabbingCursor,
    setPointerCursor,
    setDefaultCursor,
    findMostLeftPoint,
    findMostRightPoint,
    findMostLowestPoint,
    findHighestPoint,
    findShapeByKonvaAttributes,
  };
}
